import { useEffect, useMemo, useState } from 'react';
import { Box, StatusIndicator } from '@cloudscape-design/components';
import { PageLayout, PlatformTable, useAuth, useNotify, createApiClient } from '@deloitte-us-consulting/gp-sdk';

const API_URL = (import.meta.env.VITE_API_URL as string) ?? '';

interface OnboardingRequest {
  id: string;
  appId: string;
  displayName: string;
  repoUrl: string;
  submittedAt: string;
  status: 'submitted' | 'approved' | 'rejected';
  rejectionReason?: string;
  registryAppId?: string;
}
interface RegistryEntry {
  appId: string;
  enabled: boolean;
}

function statusFor(request: OnboardingRequest, liveAppIds: Set<string>) {
  if (request.status === 'rejected') return <StatusIndicator type="error">Rejected</StatusIndicator>;
  if (request.status === 'submitted') return <StatusIndicator type="pending">Submitted</StatusIndicator>;
  // Approved — "Live" isn't a stored status, it's derived by cross-referencing
  // the registry: an admin still has to finish the deploy-role/SDK-access/
  // first-deploy gates and enable it before it's actually reachable.
  return liveAppIds.has(request.appId) ? (
    <StatusIndicator type="success">Live</StatusIndicator>
  ) : (
    <StatusIndicator type="in-progress">Approved — pending deploy</StatusIndicator>
  );
}

/**
 * A builder's own onboarding submissions and their current status — the
 * read side of the onboarding-request domain. "Live" is computed here, not
 * stored: it's true exactly when the registry entry this submission produced
 * is enabled (GET /api/registry, already public to any authenticated user).
 */
export default function OnboardMine() {
  const { accessToken } = useAuth();
  const { notify } = useNotify();
  const api = useMemo(() => createApiClient(API_URL, () => accessToken), [accessToken]);

  const [requests, setRequests] = useState<OnboardingRequest[]>([]);
  const [liveAppIds, setLiveAppIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setError(null);
    try {
      const [mine, registry] = await Promise.all([
        api.get<OnboardingRequest[]>('/api/onboarding/mine'),
        api.get<RegistryEntry[]>('/api/registry'),
      ]);
      setRequests(mine);
      setLiveAppIds(new Set(registry.filter((r) => r.enabled).map((r) => r.appId)));
    } catch (e) {
      setError((e as Error).message);
      notify({ type: 'error', header: 'Could not load your submissions', content: (e as Error).message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [api]);

  return (
    <PageLayout title="My submissions" description="Apps you've submitted for platform review, and their current status.">
      <PlatformTable
        items={requests}
        loading={loading}
        error={error}
        onRetry={load}
        title="Your submissions"
        emptyTitle="No submissions yet"
        emptyDescription='Submit an app from "Submit an app" — it will show up here with its review status.'
        columnDefinitions={[
          { header: 'App ID', cell: (r) => r.appId },
          { header: 'Display name', cell: (r) => r.displayName },
          { header: 'Submitted', cell: (r) => r.submittedAt },
          { header: 'Status', cell: (r) => statusFor(r, liveAppIds) },
          {
            header: 'Detail',
            cell: (r) => (r.status === 'rejected' && r.rejectionReason ? <Box color="text-status-error">{r.rejectionReason}</Box> : '—'),
          },
        ]}
      />
    </PageLayout>
  );
}
